import { Box, type SxProps } from '@mui/material'
import { type ReactNode } from 'react'

import {
  PAGE_PADDING_X,
  PAGE_PADDING_Y,
  PROMO_WIDTH,
  SIDE_BY_SIDE
} from 'components/authentication/foresight-promo/foresight-promo-utils'
import { ForesightPromo } from 'components/authentication/foresight-promo/ForesightPromo'
import {
  AnonymousLaunchDarklyProvider,
  DEFAULT_FEATURE_FLAG_VALUES
} from 'components/LaunchDarklyProvider'
import { useFeatureFlags } from 'hooks/use-feature-flags'
import theme from 'theme'
import { isPrivateEdition } from 'utils/app-config'

const FORM_COLUMN_WIDTH = theme.space[10] as number

const formColumnSx: SxProps = {
  width: '100%',
  maxWidth: FORM_COLUMN_WIDTH,
  flexShrink: 0
}

const MAX_COLUMN_GAP = 155

const pageLayoutSx: SxProps = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '100vh',
  paddingX: PAGE_PADDING_X,
  paddingBottom: PAGE_PADDING_Y,
  [SIDE_BY_SIDE]: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    maxWidth: `calc(${
      FORM_COLUMN_WIDTH + PROMO_WIDTH + MAX_COLUMN_GAP
    }px + 2 * ${PAGE_PADDING_X})`,
    marginX: 'auto',
    paddingY: PAGE_PADDING_Y
  }
}

const promoColumnSx: SxProps = {
  width: '100%',
  maxWidth: FORM_COLUMN_WIDTH,
  [SIDE_BY_SIDE]: { width: 'auto', maxWidth: 'none' }
}

function LoginLayout({ children }: { children: ReactNode }) {
  const { foresightAdOnLogin: isEnabledForesightAdOnLogin } = useFeatureFlags()
  const isAdShown = !isPrivateEdition() && Boolean(isEnabledForesightAdOnLogin)

  // Same tree with or without the ad, so the form doesn't remount and lose what was typed if the flag loads late
  return (
    <Box sx={isAdShown ? pageLayoutSx : undefined}>
      <Box sx={isAdShown ? formColumnSx : undefined}>{children}</Box>
      {isAdShown && (
        <Box sx={promoColumnSx}>
          <ForesightPromo />
        </Box>
      )}
    </Box>
  )
}

export function LoginWithForesightPromo({ children }: { children: ReactNode }) {
  return (
    <AnonymousLaunchDarklyProvider
      flags={{
        'foresight-ad-on-login':
          DEFAULT_FEATURE_FLAG_VALUES['foresight-ad-on-login']
      }}
    >
      <LoginLayout>{children}</LoginLayout>
    </AnonymousLaunchDarklyProvider>
  )
}
